import { useEffect, useRef } from "react";
import { motion } from "framer-motion";
import { X, ChevronLeft, ChevronRight } from "lucide-react";
import { useLang } from "@/i18n/LanguageContext";
import { waLink } from "@/content/site";
import { WhatsAppIcon } from "@/components/apex/primitives";
import LedGlow from "@/components/apex/LedGlow";

/* عارض وسائط بملء الشاشة — يدعم لوحة المفاتيح وحصر التركيز */
const MediaViewer = ({ item, onClose, onPrev, onNext }) => {
  const { t, isRtl } = useLang();
  const panelRef = useRef(null);
  const closeRef = useRef(null);

  useEffect(() => {
    closeRef.current?.focus();
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") (isRtl ? onPrev : onNext)?.();
      if (e.key === "ArrowLeft") (isRtl ? onNext : onPrev)?.();
      if (e.key === "Tab" && panelRef.current) {
        const nodes = panelRef.current.querySelectorAll(
          'a[href], button:not([disabled])',
        );
        if (!nodes.length) return;
        const first = nodes[0];
        const last = nodes[nodes.length - 1];
        if (e.shiftKey && document.activeElement === first) {
          e.preventDefault();
          last.focus();
        } else if (!e.shiftKey && document.activeElement === last) {
          e.preventDefault();
          first.focus();
        }
      }
    };
    window.addEventListener("keydown", onKey);
    return () => {
      document.body.style.overflow = prevOverflow;
      window.removeEventListener("keydown", onKey);
    };
  }, [onClose, onPrev, onNext, isRtl]);

  const title = isRtl ? item.title_ar : item.title_en;
  const finish = isRtl ? item.finish_ar : item.finish_en;
  const askMsg = isRtl
    ? `مرحبًا APEX، أعجبني تصميم «${item.title_ar}» وأرغب بمعرفة الخيارات والسعر المناسب لمدخلي.`
    : `Hello APEX, I liked the "${item.title_en}" design and would like to know the options and a suitable price for my entrance.`;

  return (
    <motion.div
      className="fixed inset-0 z-[70] flex flex-col bg-[rgba(9,12,15,0.97)] backdrop-blur-sm"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      role="dialog"
      aria-modal="true"
      aria-label={title}
      data-testid="media-viewer"
      ref={panelRef}
    >
      <div className="flex items-center justify-between gap-3 px-5 py-4 sm:px-8">
        <div className="min-w-0">
          <p className="truncate text-sm font-semibold text-white">{title}</p>
          <p className="truncate text-xs text-white/60">{finish}</p>
        </div>
        <div className="flex items-center gap-2">
          {onPrev && (
            <button
              type="button"
              onClick={onPrev}
              aria-label={t.gallery.prev}
              data-testid="viewer-prev"
              className="grid h-10 w-10 place-items-center rounded-full bg-white/10 text-white transition-colors hover:bg-[var(--yellow)] hover:text-[var(--charcoal)]"
            >
              {isRtl ? <ChevronRight className="h-5 w-5" /> : <ChevronLeft className="h-5 w-5" />}
            </button>
          )}
          {onNext && (
            <button
              type="button"
              onClick={onNext}
              aria-label={t.gallery.next}
              data-testid="viewer-next"
              className="grid h-10 w-10 place-items-center rounded-full bg-white/10 text-white transition-colors hover:bg-[var(--yellow)] hover:text-[var(--charcoal)]"
            >
              {isRtl ? <ChevronLeft className="h-5 w-5" /> : <ChevronRight className="h-5 w-5" />}
            </button>
          )}
          <button
            type="button"
            ref={closeRef}
            onClick={onClose}
            aria-label={t.gallery.close}
            data-testid="viewer-close"
            className="grid h-10 w-10 place-items-center rounded-full bg-white/10 text-white transition-colors hover:bg-[var(--yellow)] hover:text-[var(--charcoal)]"
          >
            <X className="h-5 w-5" />
          </button>
        </div>
      </div>

      <div className="flex min-h-0 flex-1 items-center justify-center px-4 pb-4">
        {item.type === "video" ? (
          <video
            src={item.src}
            poster={item.poster}
            controls
            autoPlay
            playsInline
            className="max-h-full max-w-full rounded-2xl"
            data-testid="viewer-video"
          />
        ) : (
          <span className="relative inline-block max-h-full max-w-full overflow-hidden rounded-2xl">
            <img
              src={item.src}
              alt={isRtl ? item.alt_ar : item.alt_en}
              className="block max-h-[inherit] max-w-full object-contain"
              style={{ maxHeight: "calc(100vh - 220px)" }}
              data-testid="viewer-image"
            />
            <LedGlow led={item.led} />
          </span>
        )}
      </div>

      <div className="px-5 pb-[max(1.25rem,env(safe-area-inset-bottom))] sm:px-8">
        <a
          href={waLink(askMsg)}
          target="_blank"
          rel="noopener noreferrer"
          data-testid="viewer-ask-whatsapp"
          className="ap-btn-wa mx-auto flex w-full max-w-md"
        >
          <WhatsAppIcon className="h-5 w-5" />
          {t.gallery.ask}
        </a>
      </div>
    </motion.div>
  );
};

export default MediaViewer;
